import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";

const BambooHRIntegration = () => {
  const [showSecret, setShowSecret] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const formInitialValues = {
    url: "",
    api_key: "",
  };
  const formCheckoutSchema = yup.object().shape({
    url: yup.string().required("required"),
    api_key: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("bamboohr");
  const [loading, setLoading] = useState(false);
  const { handleExpandAll, handleCollapseAll } = useExpandedList();
  const { registerBambooHRIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerBambooHRIntegration(values.url.trim(), values.api_key.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("BambooHR integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !isIntegratedLoading
            ? "Once integrated, the Breeze platform will automatically fetch the relevant information from BambooHR."
            : ""
        }
        image={logos.BambooHRLogo}
        iconBorderRadius={0}
        name={"BambooHR"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>BambooHR is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <Box mb="20px">
                        <Alert severity="info">
                          <AlertTitle> Permissions Notice</AlertTitle>
                          The permissions of the user associated with the API
                          request will determine which fields and employees each
                          API request is allowed to view and/or edit.
                        </Alert>
                      </Box>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login to your BambooHR console">
                          <Typography variant={"body2"}>
                            Enter below your Tenant name in the BambooHR
                            console.
                          </Typography>
                          <Typography variant={"body2"}>
                            Your URL should look like this:{" "}
                          </Typography>
                          <Typography variant={"body2"}>
                            https://<strong>tenant-name</strong>
                            .bamboohr.com
                          </Typography>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item>
                              <Typography variant="body1">https://</Typography>
                            </Grid>
                            <Grid item>
                              <TextField
                                fullWidth
                                variant="filled"
                                name="url"
                                label="Tenant name"
                                value={values.url}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={!!touched.url && !!errors.url}
                                helperText={touched.url && errors.url}
                                disabled={guideMode}
                              />
                            </Grid>
                            <Grid item>
                              <Typography variant="body1">
                                .bamboohr.com
                              </Typography>
                            </Grid>
                          </Grid>
                          <PrimaryButton
                            onClick={() => {
                              window.open(
                                `https://${values.url}.bamboohr.com`,
                                "_blank"
                              );
                            }}
                            style={{ textTransform: "none" }}
                            disabled={!values.url}
                          >
                            Login to BambooHR
                          </PrimaryButton>
                        </GuidelineStep>
                        <GuidelineStep title="Create a new API key">
                          <Typography variant={"body2"}>
                            Click on your name in the upper right-hand corner of
                            any page to get to the user context menu.
                          </Typography>
                          <Typography variant={"body2"}>
                            If you have sufficient permissions, there will be an
                            "API Keys" option in that menu to go to the page.
                          </Typography>
                          <Typography variant={"body2"}>
                            Click on the "Add a new key" button to create a new
                            key.
                          </Typography>
                          <Typography variant={"body2"}>
                            Copy the key and paste it below.
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <TextField
                              fullWidth
                              variant="filled"
                              name="api_key"
                              label="API Key"
                              value={values.api_key}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              autoComplete="off"
                              type={showSecret ? "text" : "password"}
                              error={!!touched.api_key && !!errors.api_key}
                              helperText={touched.api_key && errors.api_key}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      aria-label="toggle password visibility"
                                      onClick={handleClickShowSecret}
                                      onMouseDown={handleMouseDownSecret}
                                    >
                                      {showSecret ? (
                                        <Visibility />
                                      ) : (
                                        <VisibilityOff />
                                      )}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              style={{ width: "700px" }}
                              disabled={guideMode}
                            />
                            <Box mt={2}>
                              <PrimaryButton type="submit" disabled={guideMode}>
                                Connect
                              </PrimaryButton>
                            </Box>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default BambooHRIntegration;
